// @ts-check
// Barras do editor (spec 2.0.0, fluxos.md seções 20.4 e 20.16; contrato 09 de fluxos, seção 17). Barra 1: voltar para os
// fluxos, o nome com o lápis, a situação, "Estatísticas" e "Sair do editor". Barra 2: as abas Editor, Configurações e
// Saída (ponto âmbar na aba com pendência), o botão de pendências com a lista agrupada, "Alterações não salvas" e os
// botões de salvar e ativar (ou "Salvar alterações" e o menu, no fluxo ativo). No celular, uma barra só com o menu.
import { html } from 'htm/preact';
import { useRef } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { groupFlowProblems, tabHasProblem } from '../../lib/flow-validation.js';
import { FlowMenuButton } from './flow-menu.js';
import { FlowStatusChip, flowProblemText } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 * @typedef {import('../../data/flows.js').FlowDetail} FlowDetail
 * @typedef {import('./flow-menu.js').FlowMenuItem} FlowMenuItem
 * @typedef {'editor' | 'settings' | 'exit'} FlowEditorTab
 */

export const FLOW_EDITOR_TABS = /** @type {ReadonlyArray<FlowEditorTab>} */ (Object.freeze(['editor', 'settings', 'exit']));

/** Aba da URL (`?tab=`), editor por padrão. @param {URLSearchParams} parameters @returns {FlowEditorTab} */
export function readFlowEditorTab(parameters) {
  const tab = parameters.get('tab');
  return tab === 'settings' || tab === 'exit' ? tab : 'editor';
}

const GROUP_KEYS = /** @type {const} */ (['ENTRY', 'NODE', 'EXIT', 'SETTINGS']);

/** Lugar e texto de uma pendência (o que a pessoa vê na lista). @param {FlowProblem} problem */
const problemPlaceKey = (problem) => [problem.area, problem.nodeId ?? '', problem.triggerId ?? '', problem.criterionId ?? '', flowProblemText(problem)].join('|');

/** @param {FlowProblem[]} problems */
function uniqueVisibleProblems(problems) {
  /** @type {Map<string, FlowProblem>} */
  const byPlace = new Map();
  for (const problem of problems) {
    const key = problemPlaceKey(problem);
    const current = byPlace.get(key);
    // o erro vence o aviso do mesmo lugar e texto
    if (!current || (current.severity !== 'ERROR' && problem.severity === 'ERROR')) byPlace.set(key, problem);
  }
  return [...byPlace.values()];
}

/**
 * Botão "2 pendências" e a lista agrupada (Entrada, Ações, Saída, Configurações); um item leva ao lugar dele.
 * @param {{ problems: FlowProblem[], isOpen: boolean, onOpenChange: (isOpen: boolean) => void, onGo: (problem: FlowProblem) => void,
 *   nodeTitle: (nodeId: string) => string }} props
 */
export function FlowProblemsButton({ problems: allProblems, isOpen, onOpenChange, onGo, nodeTitle }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  // duas pendências do mesmo lugar com o mesmo texto (dois campos vazios do passo) aparecem uma vez só
  const problems = uniqueVisibleProblems(allProblems);
  if (problems.length === 0) return null;
  const groups = groupFlowProblems(problems);
  const hasError = problems.some((problem) => problem.severity === 'ERROR');
  return html`<button type="button" ref=${anchorRef} class="flow-problems-button" data-severity=${hasError ? 'error' : 'warning'} aria-haspopup="dialog"
      aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => onOpenChange(!isOpen)} data-testid="flow-problems-button">
      <${Icon} name=${hasError ? 'IconAlertCircle' : 'IconAlertTriangle'} size=${16} />
      <span>${t('flows.editor.problems', { count: problems.length })}</span>
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => onOpenChange(false)} placement="bottom-end" width=${360} role="dialog">
      <div class="flow-problems-list" aria-label=${t('flows.editor.problemsTitle')}>
        ${GROUP_KEYS.map((key) => (groups[key].length === 0 ? null : html`<section key=${key} class="flow-problems-group">
          <h3 class="flow-problems-group-title">${t(`flows.editor.problemGroups.${key}`)}</h3>
          <ul>
            ${groups[key].map((problem) => html`<li key=${problemPlaceKey(problem)}>
              <button type="button" class="flow-problems-item" data-severity=${problem.severity === 'ERROR' ? 'error' : 'warning'}
                onClick=${() => {
                  onOpenChange(false);
                  onGo(problem);
                }}>
                <${Icon} name=${problem.severity === 'ERROR' ? 'IconAlertCircle' : 'IconAlertTriangle'} size=${14} />
                <span class="flow-problems-item-texts">
                  ${problem.nodeId ? html`<span class="flow-problems-item-step">${nodeTitle(problem.nodeId)}</span>` : null}
                  <span>${flowProblemText(problem)}</span>
                </span>
              </button>
            </li>`)}
          </ul>
        </section>`))}
      </div>
    <//>`;
}

/**
 * @param {{ flow: FlowDetail, tab: FlowEditorTab, problems: FlowProblem[], isDirty: boolean, isSaving: boolean, isMobile: boolean,
 *   problemsOpen: boolean, onProblemsOpenChange: (isOpen: boolean) => void, onGoToProblem: (problem: FlowProblem) => void, nodeTitle: (nodeId: string) => string,
 *   onTab: (tab: FlowEditorTab) => void, onBack: () => void, onExit: () => void, onStats: (() => void) | null, onRename: () => void,
 *   onSave: () => void, onSaveAndActivate: () => void, menuItems: FlowMenuItem[] }} props
 */
export function FlowEditorBars({
  flow, tab, problems, isDirty, isSaving, isMobile, problemsOpen, onProblemsOpenChange, onGoToProblem, nodeTitle, onTab, onBack, onExit, onStats, onRename,
  onSave, onSaveAndActivate, menuItems,
}) {
  const isActive = flow.status === 'ACTIVE';
  if (isMobile) {
    return html`<header class="flow-editor-bar flow-editor-bar-mobile">
      <button type="button" class="flow-icon-button" aria-label=${t('flows.editor.back')} onClick=${onBack}><${Icon} name="IconChevronLeft" size=${20} /></button>
      <button type="button" class="flow-editor-name" onClick=${onRename} title=${flow.name}>
        <span class="flow-editor-name-text">${flow.name}</span>
      </button>
      <${FlowStatusChip} status=${flow.status} inactiveReason=${flow.inactiveReason} inactiveDetails=${flow.inactiveDetails ?? null} />
      ${problems.length > 0
        ? html`<${FlowProblemsButton} problems=${problems} isOpen=${problemsOpen} onOpenChange=${onProblemsOpenChange} onGo=${onGoToProblem} nodeTitle=${nodeTitle} />`
        : null}
      <${FlowMenuButton} ariaLabel=${t('flows.editor.moreActions')} icon="IconDotsVertical" items=${[
        { key: 'settings', text: t('flows.editor.tabs.settings'), icon: 'IconSettings', onSelect: () => onTab('settings') },
        { key: 'exit', text: t('flows.editor.tabs.exit'), icon: 'IconLogout', onSelect: () => onTab('exit') },
        ...(isActive ? [] : [{ key: 'activate', text: t('flows.editor.saveAndActivate'), icon: 'IconPlayerPlay', onSelect: onSaveAndActivate }]),
        ...menuItems,
        { key: 'leave', text: t('flows.editor.exit'), icon: 'IconLogout', separatorBefore: true, onSelect: onExit },
      ]} />
    </header>`;
  }
  /** @param {FlowEditorTab} key */
  const tabLabel = (key) => t(`flows.editor.tabs.${key}`);
  return html`<header class="flow-editor-bar flow-editor-bar-top">
      <div class="flow-editor-bar-left">
        <button type="button" class="flow-back-link" onClick=${onBack}><${Icon} name="IconChevronLeft" size=${16} /><span>${t('flows.list.title')}</span></button>
        <span class="flow-editor-divider" aria-hidden="true"></span>
        <button type="button" class="flow-editor-name" onClick=${onRename} title=${t('flows.editor.rename')} data-testid="flow-editor-name">
          <span class="flow-editor-name-text">${flow.name}</span><${Icon} name="IconPencil" size=${14} />
        </button>
        <${FlowStatusChip} status=${flow.status} inactiveReason=${flow.inactiveReason} inactiveDetails=${flow.inactiveDetails ?? null} />
      </div>
      <div class="flow-editor-bar-right">
        ${onStats ? html`<button type="button" class="flow-link-button" onClick=${onStats}><${Icon} name="IconChartBar" size=${16} />${t('flows.editor.stats')}</button>` : null}
        ${onStats ? html`<span class="flow-editor-divider" aria-hidden="true"></span>` : null}
        <button type="button" class="flow-editor-exit" onClick=${onExit}>${t('flows.editor.exit')}</button>
      </div>
    </header>
    <div class="flow-editor-bar flow-editor-bar-tabs">
      <div class="flow-editor-tabs" role="tablist" aria-label=${t('flows.editor.tabsLabel')}>
        ${FLOW_EDITOR_TABS.map((key) => html`<button type="button" key=${key} role="tab" class="flow-editor-tab" aria-selected=${tab === key ? 'true' : 'false'}
          data-active=${tab === key ? 'true' : undefined} onClick=${() => onTab(key)} data-testid=${`flow-tab-${key}`}>
          <span>${tabLabel(key)}</span>
          ${tabHasProblem(problems, key) ? html`<span class="flow-editor-tab-dot" aria-label=${t('flows.editor.tabHasProblems')}></span>` : null}
        </button>`)}
      </div>
      <div class="flow-editor-bar-right">
        <${FlowProblemsButton} problems=${problems} isOpen=${problemsOpen} onOpenChange=${onProblemsOpenChange} onGo=${onGoToProblem} nodeTitle=${nodeTitle} />
        ${isDirty ? html`<span class="flow-editor-unsaved" role="status"><span class="flow-editor-unsaved-dot" aria-hidden="true"></span>${t('flows.editor.unsaved')}</span>` : null}
        ${isActive
          ? html`<${Button} title=${t('flows.editor.saveChanges')} accent="blue" size="small" disabled=${!isDirty} isLoading=${isSaving} onClick=${onSave} hotkeyLabel="Ctrl S"
              data-testid="flow-save-button" />`
          : html`<${Button} title=${t('flows.editor.save')} variant="tertiary" size="small" isLoading=${isSaving} onClick=${onSave} data-testid="flow-save-button" />
            <${Button} title=${t('flows.editor.saveAndActivate')} accent="blue" size="small" disabled=${isSaving} onClick=${onSaveAndActivate} data-testid="flow-activate-button" />`}
        ${menuItems.length > 0 ? html`<${FlowMenuButton} ariaLabel=${t('flows.editor.moreActions')} icon="IconDots" items=${menuItems} className="flow-menu-trigger flow-editor-menu" />` : null}
      </div>
    </div>`;
}

/** Faixa "Há alterações não salvas deste fluxo neste navegador." @param {{ onRestore: () => void, onDiscard: () => void }} props */
export function FlowDraftBanner({ onRestore, onDiscard }) {
  return html`<div class="flow-draft-banner" role="status">
    <${Icon} name="IconDeviceFloppy" size=${16} />
    <span>${t('flows.editor.localDraft')}</span>
    <${Button} title=${t('flows.editor.restore')} variant="secondary" size="small" onClick=${onRestore} />
    <${Button} title=${t('flows.editor.discard')} variant="tertiary" size="small" onClick=${onDiscard} />
  </div>`;
}

